import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Container, Navbar } from "react-bootstrap";
import { Outlet } from "react-router-dom";
import { Box, Toolbar, AppBar, Select, MenuItem, Typography, Collapse, IconButton } from "@mui/material";
import { changeDateFormate } from "../../../global/globalFunctions";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import IconMenu from "./IconMenu";


const SinglePageDashboard = ({ header = '' }) => {
  const { MEData } = useSelector((state) => state.HomePageReducer);
  const [mode] = useState("light");
  const { i18n } = useTranslation();

  const ALERT_CONFIG = [
    { id: 1, severity: "warning", backgroundColor: "#fbc828", message: `Your license will expire on ${changeDateFormate(MEData.date)} . Please renew and complete the payment to continue using the software.` },
    { id: 2, severity: "info", backgroundColor: "#0288D1", message: "This alert is hidden because show is false." },
    { id: 3, severity: "error", backgroundColor: "#E68383", message: "Scheduled maintenance tonight at 11 PM. Expect brief downtime." },
  ];
  const SEVERITY_ICONS = {
    warning: <WarningIcon fontSize="small" />,
    error: <ErrorOutlineIcon fontSize="small" />,
    info: <InfoOutlinedIcon fontSize="small" />,
    success: <CheckCircleOutlineIcon fontSize="small" />,
  };
  const ALERT_STORAGE_KEY = "dismissed_alerts";

  const getDismissed = () => {
    try { return JSON.parse(localStorage.getItem(ALERT_STORAGE_KEY)) || []; }
    catch { return []; }
  };

  const saveDismissed = (ids) => {
    localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(ids));
  };

  const buildAlerts = () => {
    const dismissed = getDismissed();
    return ALERT_CONFIG.map((alert) => {
      let shouldShow = false;
      if (alert.id === 1) {
        shouldShow = !!MEData?.isAlertShow && !dismissed.includes(alert.id);
      } else {
        shouldShow = false;
      }
      return { ...alert, show: shouldShow };
    });
  };

  const [alerts, setAlerts] = useState(buildAlerts);

  useEffect(() => {
    setAlerts(buildAlerts());
  }, [MEData?.isAlertShow]);

  const dismissAlert = (id) => {
    const dismissed = getDismissed();
    if (!dismissed.includes(id)) {
      saveDismissed([...dismissed, id]);
    }
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, show: false } : a))
    );
  };

  useEffect(() => {
    if (!MEData?.isAlertShow) {
      const dismissed = getDismissed().filter((id) => id !== 1);
      saveDismissed(dismissed);
    }
  }, [MEData?.isAlertShow]);

  const visibleAlerts = alerts.filter((a) => a.show === true);
  const alertBannerHeight = visibleAlerts.length * 48;

  useEffect(() => {
    const lang = localStorage.getItem("lang") || "en";
    i18n.changeLanguage(lang);
  }, []);

  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);

  const handleLanguageChange = (event) => {
    const lang = event.target.value;
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>

        {/* ================= ALERT BANNER ================= */}
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.appBar + 100 }}>
          {alerts.map((alert) => (
            <Collapse key={alert.id} in={alert.show} timeout={300} unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: alert.backgroundColor,
                  color: "#253858",
                  px: 3,
                  py: 0.75,
                  minHeight: 48,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", opacity: 0.75, color: "#253858" }}>
                    {SEVERITY_ICONS[alert.severity] ?? SEVERITY_ICONS.warning}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#253858", lineHeight: 1.4, fontSize: "15px" }}>
                    {alert.message}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  // onClick={() => dismissAlert(alert.id)}
                  sx={{ color: "#253858", ml: 2, "&:hover": { bgcolor: "rgba(0,0,0,0.1)" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Collapse>
          ))}
        </Box>

        {/* ================= APP BAR ================= */}
        <AppBar position="fixed" sx={{ top: alertBannerHeight, transition: "top 0.3s ease" }}>
          <Navbar style={{ paddingLeft: "3%", paddingRight: "3%", backgroundColor: "#81abd3" }}>
            <Container style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Navbar.Brand style={{ color: "white" }}><b>{header}</b></Navbar.Brand>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Select
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  size="small"
                  sx={{ bgcolor: "white", borderRadius: 1, minWidth: 110 }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="ar">العربية</MenuItem>
                  <MenuItem value="ur">Urdu</MenuItem>
                  <MenuItem value="gr">German</MenuItem>
                </Select>
                <IconMenu />
              </Box>
            </Container>
          </Navbar>
        </AppBar>

        {/* ================= MAIN CONTENT ================= */}
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Toolbar sx={{ mt: `${alertBannerHeight}px`, transition: "margin 0.3s ease" }} />
          <Outlet />
        </Box>

      </Box>
    </ThemeProvider>
  );
};

export default SinglePageDashboard;